import { React, useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useToast } from "./components/ToastContext";
import LoginNav from "./components/LoginNav";

const LoginScreen = ({
  onRegister,
  onLogin,
  authError,
  isLoading,
  verifyCode,
  sendVerificationEmail,
  navigateTo,
}) => {
  const toast = useToast();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    verificationCode: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    setFormData({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      verificationCode: "",
    });

    setValidationError("");
  }, [isLoginMode]);

  // const LinkedInLoginButton = () => {
  //   const backendUrl =
  //     process.env.REACT_APP_API_BASE || "http://localhost:5000";
  //   return (
  //     <button
  //       onClick={() =>
  //         (window.location.href = `${backendUrl}/api/auth/linkedin/login`)
  //       }
  //       className="buttons-indigo"
  //     >
  //       <Linkedin size={16} />
  //       <span className="font-medium"> Continue with LinkedIn</span>
  //     </button>
  //   );
  // };

  const GoogleLoginButton = () => {
    const backendUrl =
      process.env.REACT_APP_API_BASE || "http://localhost:5000";
    return (
      <button
        onClick={() =>
          (window.location.href = `${backendUrl}/api/auth/google/login`)
        }
        className="buttons-indigo"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Sign in with Google
      </button>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoginMode) {
      // Call App.js handler
      await onLogin(formData.email, formData.password);
    } else {
      // New user
      if (verifying) {
        // verify code
        const verification = await verifyCode(
          formData.email,
          formData.verificationCode,
        );

        if (!verification.verified) {
          toast.error(
            "That code didn't match. Please check your email and try again.",
          );
          return;
        }

        setVerifying(false);

        // Call App.js handler
        await onRegister(
          formData.email,
          formData.password,
          formData.firstName,
          formData.lastName,
        );
        navigateTo("subscriptionPlans", true);
      } else {
        const errorList = [];
        if (formData.password.length < 8) {
          errorList.push("Password must be at least 8 characters");
        }

        if (!/[A-Z]/.test(formData.password)) {
          errorList.push("Password must contain at least one uppercase letter");
        }

        if (!/[a-z]/.test(formData.password)) {
          errorList.push("Password must contain at least one lowercase letter");
        }

        if (!/\d/.test(formData.password)) {
          errorList.push("Password must contain at least one number");
        }

        setValidationError(errorList.join("\n"));
        if (errorList.length === 0) {
          // send verification email
          const result = await sendVerificationEmail(formData.email);
          if ("error" in result) {
            // Backend may already return the friendly "already registered" message;
            // either way, we surface a curated string and stop registration.
            if (
              result.error === "Email already registered" ||
              (result.status >= 400 && result.status < 500)
            ) {
              toast.error(
                result.error === "Email already registered"
                  ? "This email is already registered with ApplicantHQ. Please log in instead."
                  : result.error,
              );
              return;
            }
            toast.error(result.error);
            return;
          }
          setVerifying(true);
        }
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="flex-1 bg-gradient-to-br flex flex-col">
      <LoginNav navigateTo={navigateTo} currentScreen="login" />
      <div className="flex-1 flex items-center justify-center py-8 px-4">
        <div className="bg-offwhite rounded-2xl shadow-xl p-8 max-w-2xl w-full">
          <div className="text-center">
            <img
              src="/assets/Applicant_logo_clip.png"
              className="w-50 h-50 max-auto"
              alt="Applicant logo"
            />
            <p className="text-gray-600">Let's log in to get started!</p>
          </div>

          <div className="buttons-container">
            {/* <LinkedInLoginButton/> */}
            <GoogleLoginButton />
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or use email</span>
            </div>
          </div>

          {/* Login/Register Toggle */}
          <div className="buttons-container">
            <button
              type="button"
              onClick={() => setIsLoginMode(true)}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                isLoginMode ? "buttons-pressed" : "buttons-indigo"
              }`}
            >
              {isLoginMode ? <p>Logging in</p> : <p>Login</p>}
            </button>
            <button
              type="button"
              onClick={() => setIsLoginMode(false)}
              className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
                !isLoginMode ? "buttons-pressed" : "buttons-indigo"
              }`}
            >
              {isLoginMode ? <p>Sign up</p> : <p>Signing up</p>}
            </button>
          </div>

          {/* Error Message */}
          {(authError || validationError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {authError}
              {validationError}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLoginMode && !verifying && (
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First Name"
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required={!isLoginMode}
                />
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last Name"
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required={!isLoginMode}
                />
              </div>
            )}

            {!isLoginMode && verifying ? (
              <div>
                <input
                  type="text"
                  name="verificationCode"
                  value={formData.verificationCode}
                  onChange={handleChange}
                  placeholder="Verification Code"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
                <div className="mb-4 p-3 rounded-lg text-gray-600 text-xs">
                  Please enter the verification code sent to your email. If you
                  don't see it, check your spam folder or try registering again
                  to receive a new code.
                </div>
              </div>
            ) : (
              <div className="grid grid-rows-2 gap-4">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                />
                {!isLoginMode && (
                  <p className="text-xs text-gray-500 text-center mt-4">
                    For best results, use a personal email address (Gmail,
                    Outlook). Organizational emails may block verification
                    codes.
                  </p>
                )}

                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Password"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="password-toggle-btn"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            )}
            <div className="buttons-container">
              <button
                type="submit"
                disabled={isLoading}
                className="buttons-indigo"
              >
                {isLoading
                  ? "Processing..."
                  : isLoginMode
                    ? "Sign In"
                    : verifying
                      ? "Verify Account"
                      : "Create Account"}
              </button>
              {!isLoginMode && verifying && (
                <a
                  className="text-xs text-indigo-600"
                  style={{
                    display: "block",
                    textAlign: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => sendVerificationEmail(formData.email)}
                >
                  Resend verification code
                </a>
              )}
            </div>
            {isLoginMode && (
              <a
                className="text-xs text-indigo-600"
                style={{
                  display: "block",
                  textAlign: "center",
                  cursor: "pointer",
                }}
                onClick={() => {
                  navigateTo("reset");
                }}
              >
                Forgot Password?
              </a>
            )}
          </form>
          <p className="text-xs text-gray-500 text-center mt-4">
            {isLoginMode ? (
              <>
                Don't have an account?{" "}
                <span
                  className="text-indigo-600 cursor-pointer hover:underline font-medium"
                  onClick={() => setIsLoginMode(false)}
                >
                  Sign up here
                </span>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <span
                  className="text-indigo-600 cursor-pointer hover:underline font-medium"
                  onClick={() => setIsLoginMode(true)}
                >
                  Log in here
                </span>
              </>
            )}
          </p>
          <p className="text-xs text-gray-500 text-center mt-4">
            By signing in or creating an account, you agree to our{" "}
            <span className="legal-link" onClick={() => navigateTo("terms")}>
              Terms of Service
            </span>{" "}
            and{" "}
            <span className="legal-link" onClick={() => navigateTo("privacy")}>
              Privacy Policy
            </span>
            .
          </p>
        </div>
      </div>
      <div className="w-full flex justify-center items-center py-2">
        <span className="text-gray-500 text-xs">
          &copy; {new Date().getFullYear()} ApplicantHQ. All rights reserved.
        </span>
      </div>
    </div>
  );
};

export default LoginScreen;
